// src/services/support.ts
import api from "../lib/api";

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface SupportMessage {
  id: string;
  userId: string;
  message: string;
  isFromAdmin: boolean;
  adminId?: string;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    photoUrl?: string;
    email?: string;
  };
  admin?: {
    id: string;
    firstName: string;
    lastName: string;
    photoUrl?: string;
  };
}

export interface SupportConversation {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    photoUrl?: string;
    email: string;
    userType: string;
  };
  lastMessage: {
    message: string;
    createdAt: string;
    isFromAdmin: boolean;
  } | null;
  unreadCount: number;
}

// ============================================
// USER SUPPORT ENDPOINTS
// ============================================

/**
 * Send a support message
 */
export async function sendSupportMessage(message: string): Promise<SupportMessage> {
  const { data } = await api.post('/api/support/messages', { message });
  return data.data.message;
}

/**
 * Get my support conversation with VCG Support
 */
export async function getMySupportConversation(limit = 50, offset = 0): Promise<SupportMessage[]> {
  const { data } = await api.get('/api/support/conversation', {
    params: { limit, offset }
  });
  return data.data.messages;
}

/**
 * Get unread support messages count
 */
export async function getUnreadSupportCount(): Promise<number> {
  const { data } = await api.get('/api/support/unread-count');
  return data.data.unreadCount;
}

// ============================================
// ADMIN SUPPORT ENDPOINTS
// ============================================

/**
 * Admin: Get all support conversations
 */
export async function getAllSupportConversations(): Promise<SupportConversation[]> {
  const { data } = await api.get('/api/support/admin/conversations');
  return data.data.conversations;
}

/**
 * Admin: Get support conversation with a specific user
 */
export async function getUserSupportConversation(userId: string, limit = 50, offset = 0): Promise<SupportMessage[]> {
  const { data } = await api.get(`/api/support/admin/conversation/${userId}`, {
    params: { limit, offset }
  });
  return data.data.messages;
}

/**
 * Admin: Reply to support message
 */
export async function replySupportMessage(userId: string, message: string): Promise<SupportMessage> {
  const { data } = await api.post(`/api/support/admin/reply/${userId}`, { message });
  return data.data.message;
}
